/**
 * @lines98/client/services/daily-api — typed wrappers вокруг daily-board server endpoints.
 *
 * Per dispatch_t-7 §2 C3 + core-daily-board.md §3.1/§8: the server is the single
 * source of truth для `daily_seed` и `date_of_day`. Client NEVER derives them
 * самостоятельно (anti-cheat.md §7.13: «daily_seed определяется серверным временем,
 * клиентскому не доверяем»).
 *
 * Endpoints wrapped:
 *   - `GET /daily/seed` — returns `{ date_of_day, seed, server_time_utc }`.
 *     Source-of-truth: `packages/server/src/routes/daily-seed.ts`.
 *
 * Bearer transport: api-client.ts attaches Authorization header automatically
 * if identity exists (per ADR-0006 §D2). T-2 endpoint is technically public,
 * но identity SHOULD be present at this point (main.ts boots identity before
 * mounting any screen).
 *
 * Error handling: returns а discriminated union ({ ok: true, ... } | { ok: false, error }).
 * Network / 5xx → `{ ok: false, error: 'network' | 'server_error' }`. UI surfaces
 * через retry button per dispatch §2 C1 (idle state «не удалось загрузить, повторить»).
 *
 * In-memory seed cache (T-12, owner-asked 2026-05-31 — anti-cheat.md §3A.1 note):
 *   `getDailySeed()` populates а module-level session cache on success;
 *   `getCachedDailySeed(now?)` reads it synchronously. The daily-board screen uses
 *   the synchronous cache to paint the idle board IMMEDIATELY on re-mount (no
 *   loading flash on navigation игра↔доска-дня), instead of awaiting the network.
 *   `prefetchDailySeed()` (T-12 follow-up, 2026-05-31) fires this fetch ONCE on
 *   boot (main.ts) so the cache is warm before the FIRST visit to the daily board
 *   too — removing the initial loading flash. Best-effort: a prefetch error is
 *   swallowed and never blocks boot (the screen still self-fetches on a miss).
 *
 *   **Safety (anti-cheat.md §3A.1 note):** caching today's daily seed is а safe
 *   UX optimization, NOT an anti-cheat weakness:
 *     (a) the seed is not secret — it's already handed to every client on board
 *         open and is identical for everyone (anti-cheat never relied on hiding it);
 *     (b) the score is verified by server re-simulation against the
 *         server-authoritative seed — the client cannot pick the seed for а
 *         counted game (anti-cheat.md §3.3 / §7.4 `invalid_seed` backstop);
 *     (c) «one attempt/day» is tracked server-side (`daily_attempts`), not by this
 *         cache;
 *     (d) the foreign-replay gate (§3A.2) does not touch the seed.
 *   **Guardrails (mandatory, enforced here):**
 *     (1) The cache lives ONLY in daily-flow. Casual / practice / any non-daily
 *         code-path never reads it (Vector 1 §3A.1 — today's seed must not leak
 *         into non-counted modes). `getCachedDailySeed` is consumed solely by
 *         daily-board-screen; `startNewGame` (casual) keeps its `Math.random()`
 *         seed unchanged.
 *     (2) Cache key = `date_of_day`. On rollover the cache is treated as stale
 *         (miss) so the client re-fetches the new day's seed. Validity is computed
 *         from `serverTimeUtc` + elapsed wall-clock (not bare local hours) so a
 *         skewed device clock cannot extend a stale seed past the boundary; the
 *         server `invalid_seed` reject (§7.4) is the final backstop.
 *   Future agents: do NOT flag this client seed-cache as a leak — it is a vetted
 *   owner-decision (anti-cheat.md §3A.1 note, 2026-05-31).
 *
 * Cross-references:
 *   - dispatch_t-7_game-developer.md §2 C3.
 *   - core-daily-board.md §3.1 (date_of_day) + §3.2 (daily_seed) + §7.3 (rollover).
 *   - anti-cheat.md §3A.1 note (client seed-cache — safe optimization + guardrails).
 *   - packages/server/src/routes/daily-seed.ts (contract source).
 *   - services/api-client.ts (Bearer injection + envelope shape).
 *   - @lines98/shared/util — dateOfDayFromUtc (rollover-boundary date math).
 */

import { dateOfDayFromUtc } from '@lines98/shared/util';

import { apiClient } from './api-client.js';

// =============================================================================
// Types
// =============================================================================

/**
 * Successful response от `GET /daily/seed`. Matches the server-side
 * `DailySeedResponse` shape (snake_case JSON, camelCase on this side after
 * mapping).
 */
export interface DailySeedSuccess {
  readonly ok: true;
  /** `YYYY-MM-DD` per `DAILY_ROLLOVER_BOUNDARY_UTC_HOURS = 21`. */
  readonly dateOfDay: string;
  /** uint32 seed feeding `startState(seed)`. */
  readonly seed: number;
  /**
   * Authoritative server clock в ISO-8601 UTC. Client uses for countdown
   * synchronization (anti-cheat.md §7.13: don't trust local clock).
   */
  readonly serverTimeUtc: string;
}

export type DailySeedError =
  /** Network / fetch reject — retry-able. */
  | { readonly ok: false; readonly error: 'network' }
  /** 5xx или unexpected response shape — retry-able. */
  | { readonly ok: false; readonly error: 'server_error'; readonly status: number }
  /** 4xx — likely rate-limit (per-IP 100/min); surface and retry-after. */
  | { readonly ok: false; readonly error: 'rate_limited'; readonly status: number };

export type DailySeedResult = DailySeedSuccess | DailySeedError;

// =============================================================================
// Internal types (server wire shape — snake_case)
// =============================================================================

interface DailySeedWireResponse {
  date_of_day: string;
  seed: number;
  server_time_utc: string;
}

// =============================================================================
// In-memory session cache (T-12) — see module header for the anti-cheat.md
// §3A.1 safety analysis + guardrails. Daily-flow ONLY.
// =============================================================================

interface SeedCacheEntry {
  readonly result: DailySeedSuccess;
  /** Authoritative server clock at fetch time (ms epoch, from serverTimeUtc). */
  readonly serverFetchedAtMs: number;
  /** Local wall-clock at fetch time (ms epoch) — anchors elapsed-time math. */
  readonly localFetchedAtMs: number;
}

let seedCache: SeedCacheEntry | null = null;

/**
 * Effective server-time NOW = recorded server time + elapsed local wall-clock
 * since the fetch. Anchored to the server clock (anti-cheat.md §3A.1 guardrail 2:
 * don't trust bare local hours for rollover validity). Monotonic-ish: clock skew
 * between fetch and read shifts both terms together, so a backwards device-clock
 * jump cannot revive a stale cache past the boundary.
 */
function effectiveServerNowMs(entry: SeedCacheEntry, localNowMs: number): number {
  return entry.serverFetchedAtMs + (localNowMs - entry.localFetchedAtMs);
}

/**
 * Synchronously read the cached daily seed, OR `null` if absent / stale.
 *
 * **Stale = rollover crossed.** The cache is valid only while the effective
 * server time still maps to the cached `date_of_day` (per `dateOfDayFromUtc`,
 * the same 21:00-UTC boundary used everywhere). Once the boundary is crossed the
 * cached seed belongs to a past day → treated as a miss → caller re-fetches.
 *
 * Daily-flow ONLY (anti-cheat.md §3A.1 guardrail 1): casual / non-daily paths
 * must never call this — today's seed must not leak into non-counted modes.
 *
 * @param now — current local epoch ms (defaults to `Date.now()`; injectable for tests).
 * @returns the cached `DailySeedSuccess`, or `null` on miss / staleness.
 */
export function getCachedDailySeed(now: number = Date.now()): DailySeedSuccess | null {
  if (!seedCache) return null;
  const effectiveNow = effectiveServerNowMs(seedCache, now);
  if (dateOfDayFromUtc(effectiveNow) !== seedCache.result.dateOfDay) {
    // Rollover crossed since fetch → cached seed is for a past day. Drop it so
    // the next fetch repopulates with the new day's seed.
    seedCache = null;
    return null;
  }
  return seedCache.result;
}

/**
 * Invalidate the in-memory seed cache. Called when a live countdown reaches zero
 * (rollover in an open tab, core-daily-board.md §5.4) so the next navigation to
 * the daily screen re-fetches the new day's seed instead of repainting the stale
 * board. Also used by tests to reset state.
 */
export function clearDailySeedCache(): void {
  seedCache = null;
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Fetch the current `daily_seed` + `date_of_day` from the server.
 *
 * Idempotent от network perspective (GET, no state-mutation). Caller may invoke
 * once per session OR once на rollover-tick — countdown component drives the
 * latter case via setInterval, but T-7 scope is per-session fetch only (rollover
 * auto-refetch belongs to T-9 — dispatch §3 «out of scope»).
 *
 * @returns `DailySeedResult` discriminated union. UI branches on `.ok`.
 */
export async function getDailySeed(): Promise<DailySeedResult> {
  let response;
  try {
    response = await apiClient.get<DailySeedWireResponse>('/daily/seed');
  } catch {
    // ApiClientError (network reject) — caller retries with backoff.
    return { ok: false, error: 'network' };
  }

  if (!response.ok) {
    if (response.status === 429) {
      return { ok: false, error: 'rate_limited', status: response.status };
    }
    return { ok: false, error: 'server_error', status: response.status };
  }

  const body = response.body;
  // Defensive shape check — server response should always match но если server
  // pushes а breaking change accidentally, fail loudly через `server_error`,
  // not с а silent NaN.
  if (
    typeof body !== 'object' ||
    body === null ||
    typeof (body as DailySeedWireResponse).date_of_day !== 'string' ||
    typeof (body as DailySeedWireResponse).seed !== 'number' ||
    typeof (body as DailySeedWireResponse).server_time_utc !== 'string'
  ) {
    return { ok: false, error: 'server_error', status: response.status };
  }

  const success: DailySeedSuccess = {
    ok: true,
    dateOfDay: body.date_of_day,
    seed: body.seed >>> 0,
    serverTimeUtc: body.server_time_utc,
  };

  // Populate the in-memory session cache (T-12). `serverTimeUtc` anchors the
  // rollover-validity math; if it's unparseable we still return success but skip
  // caching (a bad timestamp must not let us serve a never-expiring seed).
  const serverMs = Date.parse(success.serverTimeUtc);
  if (Number.isFinite(serverMs)) {
    seedCache = {
      result: success,
      serverFetchedAtMs: serverMs,
      localFetchedAtMs: Date.now(),
    };
  }

  return success;
}

/**
 * Fire-and-forget background prefetch of the daily seed (T-12 follow-up, owner-
 * asked 2026-05-31). Called once on boot (main.ts game-route) to warm the
 * in-memory seed-cache BEFORE the player reaches the daily board, so the daily
 * screen paints `renderIdle` immediately via `getCachedDailySeed()` — no loading
 * flash on the FIRST navigation to the daily board.
 *
 * This is exactly the same legal `GET /daily/seed` with the same anti-cheat
 * guardrails (§3A.1 note: seed isn't secret, score is server-verified, the cache
 * lives only in daily-flow) — just earlier in time. Errors are swallowed: an
 * offline prefetch must never break boot. On a prefetch failure the cache simply
 * stays cold and the daily screen falls back to its normal loading → fetch →
 * error-state path.
 *
 * `getDailySeed` is a GET with no state mutation; if the player races to the
 * daily board before this resolves, the screen's own boot fetch handles it.
 */
export function prefetchDailySeed(): void {
  void getDailySeed().catch(() => {
    /* best-effort prefetch — offline / error is fine, cache stays cold */
  });
}
